import { PDFDocument } from "pdf-lib";
import pdfMake from "pdfmake/build/pdfmake";
import headerImage from "../assets/images/header.png";
import { getClientId } from "../config/domain";
import { fontData } from "../fonts/vfs_fonts_times.js";
import { sendDownloadLog, upsertRequestDocument } from "../services/firestore";
import {
  cleanText,
  normalizeMarkdownText,
  preprocessMarkdown,
} from "./markdownEngine";

window.pdfMake = pdfMake;
// VFS'in tanımlı olduğundan emin ol
if (!pdfMake.vfs) {
  pdfMake.vfs = {};
}
pdfMake.vfs["Times-New-Roman-Regular.ttf"] =
  fontData["Times-New-Roman-Regular.ttf"];
pdfMake.vfs["Times-New-Roman-Bold.ttf"] = fontData["Times-New-Roman-Bold.ttf"];
pdfMake.vfs["Times-New-Roman-BoldItalic.ttf"] =
  fontData["Times-New-Roman-BoldItalic.ttf"];
pdfMake.vfs["Times-New-Roman-Italic.ttf"] =
  fontData["Times-New-Roman-Italic.ttf"];

// Times fontData Roman font tanımlaması
pdfMake.fonts = {
  // Times New Roman fontları
  TimesNewRoman: {
    normal: "Times-New-Roman-Regular.ttf",
    bold: "Times-New-Roman-Bold.ttf",
    italics: "Times-New-Roman-Italic.ttf",
    bolditalics: "Times-New-Roman-BoldItalic.ttf",
  },
  // Roboto fonts from CDN for testing
  Roboto: {
    normal: "https://fonts.cdnfonts.com/s/15884/SourceSerifPro-Regular.woff",
    bold: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf",
    italics:
      "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf",
    bolditalics:
      "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf",
  },
};

// Test font loading
console.log("pdfMake fonts defined:", Object.keys(pdfMake.fonts));

// Process text for formatting (bold and italic) - PDF specific - IMPROVED
const processFormattedText = (text) => {
  // If no formatting markers, return as is
  if (!text.includes("**") && !text.includes("_")) {
    return [{ text: text }];
  }

  const parts = [];
  let currentText = text;

  // Process both bold (**text**) and italic (_text_) formatting
  // Use a more robust approach to handle nested and adjacent formatting

  // First, let's handle the text step by step
  while (currentText.length > 0) {
    let nextBoldStart = currentText.indexOf("**");
    let nextItalicStart = currentText.indexOf("_");

    // If no more formatting markers, add remaining text and break
    if (nextBoldStart === -1 && nextItalicStart === -1) {
      if (currentText.trim()) {
        parts.push({ text: currentText, fontFeatures: ["kern"] });
      }
      break;
    }

    // Determine which marker comes first
    let nextMarkerStart = -1;
    let markerType = "";

    if (nextBoldStart !== -1 && nextItalicStart !== -1) {
      if (nextBoldStart < nextItalicStart) {
        nextMarkerStart = nextBoldStart;
        markerType = "bold";
      } else {
        nextMarkerStart = nextItalicStart;
        markerType = "italic";
      }
    } else if (nextBoldStart !== -1) {
      nextMarkerStart = nextBoldStart;
      markerType = "bold";
    } else if (nextItalicStart !== -1) {
      nextMarkerStart = nextItalicStart;
      markerType = "italic";
    }

    // Add text before the marker
    if (nextMarkerStart > 0) {
      const beforeText = currentText.substring(0, nextMarkerStart);
      if (beforeText.trim()) {
        parts.push({ text: beforeText, fontFeatures: ["kern"] });
      }
    }

    // Process the formatted text
    if (markerType === "bold") {
      const endIndex = currentText.indexOf("**", nextMarkerStart + 2);
      if (endIndex === -1) {
        // No closing **, treat rest as normal text
        const remainingText = currentText.substring(nextMarkerStart);
        if (remainingText.trim()) {
          parts.push({ text: remainingText, fontFeatures: ["kern"] });
        }
        break;
      }

      const boldText = currentText.substring(nextMarkerStart + 2, endIndex);
      if (boldText.trim()) {
        parts.push({ text: boldText, bold: true, fontFeatures: ["kern"] });
      }

      currentText = currentText.substring(endIndex + 2);
    } else if (markerType === "italic") {
      const endIndex = currentText.indexOf("_", nextMarkerStart + 1);
      if (endIndex === -1) {
        // No closing _, treat rest as normal text
        const remainingText = currentText.substring(nextMarkerStart);
        if (remainingText.trim()) {
          parts.push({ text: remainingText, fontFeatures: ["kern"] });
        }
        break;
      }

      const italicText = currentText.substring(nextMarkerStart + 1, endIndex);
      if (italicText.trim()) {
        parts.push({ text: italicText, italics: true, fontFeatures: ["kern"] });
      }

      currentText = currentText.substring(endIndex + 1);
    }
  }

  // Filter out empty parts and ensure we have at least one part
  const filteredParts = parts.filter((part) => part.text && part.text.trim());
  return filteredParts.length > 0
    ? filteredParts
    : [{ text: text, fontFeatures: ["kern"] }];
};

// Helper function to convert image to base64
const imageToBase64 = async (imagePath) => {
  try {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting image to base64:", error);
    return null;
  }
};

// Promisify pdfMake getBuffer
const getPdfBuffer = (docDefinition) =>
  new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getBuffer((buffer) => resolve(buffer));
    } catch (err) {
      reject(err);
    }
  });

// Merge main PDF buffer with additional PDF URLs using pdf-lib
const mergeAndDownloadPdfs = async (
  mainBuffer,
  additionalPdfUrls,
  filename
) => {
  const mergedPdf = await PDFDocument.create();

  const mainDoc = await PDFDocument.load(mainBuffer);
  const mainPages = await mergedPdf.copyPages(
    mainDoc,
    mainDoc.getPageIndices()
  );
  mainPages.forEach((page) => mergedPdf.addPage(page));

  for (const url of additionalPdfUrls) {
    try {
      const response = await fetch(url);
      const bytes = await response.arrayBuffer();
      const additionalDoc = await PDFDocument.load(bytes);
      const pages = await mergedPdf.copyPages(
        additionalDoc,
        additionalDoc.getPageIndices()
      );
      pages.forEach((page) => mergedPdf.addPage(page));
    } catch (err) {
      console.error("Error loading additional PDF:", url, err);
    }
  }

  const mergedBytes = await mergedPdf.save();
  const blob = new Blob([mergedBytes], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(blobUrl);
};

// PDF generation main function
export const generatePDF = async (
  petitionContent,
  hasPlaceholdersFunc,
  setModalConfig,
  setShowModal
) => {
  // Check for placeholders first
  const placeholderCheck = hasPlaceholdersFunc();
  if (placeholderCheck.hasPlaceholders) {
    const missingValues = placeholderCheck.placeholders.join(", ");
    setModalConfig({
      type: "pdf",
      message: `Belgede şu eksik değerler bulunuyor: ${missingValues}. Word formatında indirmek ister misiniz?`,
      buttons: [
        {
          text: "Word İndir",
          type: "primary",
          onClick: () => {
            setShowModal(false);
            // This should be handled by the caller
            // proceedWithDOCXGeneration();
          },
        },
        {
          text: "İptal Et",
          type: "secondary",
          onClick: () => setShowModal(false),
        },
      ],
    });
    setShowModal(true);
    return { shouldProceed: false, proceedWithWord: true };
  }

  // If no placeholders, proceed with PDF generation
  return { shouldProceed: true, proceedWithWord: false };
};

// Proceed with PDF generation
export const proceedToPDFGeneration = async (
  petitionContent,
  documentData = null,
  selectedTemplateId = null,
  dictionary = null,
  userEmail = null,
  requestId = null,
  documentOrder = null
) => {
  // Set downloading status indicator
  const downloadBtn = document.getElementById("download-summary-btn");
  downloadBtn.disabled = true;

  try {
    // Get the formatted content that has proper markdown line breaks
    let markdownText = normalizeMarkdownText(petitionContent);

    const cleanedMarkdownText = cleanText(markdownText);
    const preprocessedText = preprocessMarkdown(cleanedMarkdownText);
    const lines = preprocessedText.split("\n");

    // PDF dökümanı için içerik tanımla
    const content = [];
    const styles = {
      title: {
        fontSize: 20,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 10],
      },
      sectionTitle: {
        fontSize: 16,
        bold: true,
        alignment: "justify",
        margin: [0, 15, 0, 5],
      },
      subSectionTitle: {
        fontSize: 13,
        bold: true,
        alignment: "justify",
        margin: [0, 10, 0, 5],
      },
      paragraph: {
        fontSize: 11,
        alignment: "justify",
        margin: [0, 1, 0, 0],
      },
      paragraphIndent: {
        fontSize: 11,
        alignment: "justify",
        margin: [9, 1, 0, 0], // 9pt left margin for indentation (reduced from 18)
        leadingIndent: 16, // 36pt first line indent
      },
      label: {
        fontSize: 11,
        alignment: "justify",
        margin: [0, 1, 0, 0],
        italics: true,
      },
      labelIndent: {
        fontSize: 11,
        alignment: "justify",
        margin: [9, 1, 0, 0], // 9pt left margin for indentation (reduced from 18)
        italics: true,
        leadingIndent: 16, // 36pt first line indent
      },
      // For medium weight text
      medium: {
        fontSize: 11,
        alignment: "justify",
        margin: [0, 1, 0, 0],
        bold: true,
      },
      mediumIndent: {
        fontSize: 11,
        alignment: "justify",
        margin: [9, 1, 0, 0], // 9pt left margin for indentation (reduced from 18)
        bold: true,
        leadingIndent: 16, // 36pt first line indent
      },
      labelMedium: {
        fontSize: 11,
        alignment: "justify",
        margin: [0, 1, 0, 0],
        italics: true,
        bold: true, // Use bold to simulate medium weight
      },
      labelMediumIndent: {
        fontSize: 11,
        alignment: "justify",
        margin: [9, 1, 0, 0], // 9pt left margin for indentation (reduced from 18)
        italics: true,
        bold: true, // Use bold to simulate medium weight
      },
      listItem: {
        fontSize: 11,
        alignment: "justify",
        margin: [0, 0, 0, 0], // No margins for list items to avoid spacing
      },
    };

    // Identify and track consecutive numbered list items
    let currentListItems = [];
    let currentListStartNumber = null;

    // Helper function to add accumulated list items
    const addNumberedList = () => {
      if (currentListItems.length > 0) {
        content.push({
          ol: currentListItems,
          start: currentListStartNumber,
          separator: "", // Let pdfMake handle the default separator
          margin: [0, 0, 0, 0], // No margin around the overall list
          alignment: "justify", // Add justify alignment to numbered lists
        });
        // Reset for next list
        currentListItems = [];
        currentListStartNumber = null;
      }
    };

    // Markdown içeriğini PDF içeriğine dönüştür
    lines.forEach((line, index) => {
      // Clean each line again to ensure all problematic characters are removed
      const cleanedLine = cleanText(line);

      // Check for the specific markers we defined
      const isDoubleNewline = cleanedLine.includes("[[DOUBLE_NEWLINE]]");
      const hasTabIndent = cleanedLine.includes("[[TAB_INDENT]]");

      // Remove the markers for actual content processing
      const lineWithBreaks = cleanedLine
        .replace(/\[\[DOUBLE_NEWLINE\]\]/g, "")
        .replace(/\[\[TAB_INDENT\]\]/g, "")
        .replace(/\[\[SINGLE_LINEBREAK\]\]/g, "");

      // Check if current line is a numbered list item (more specific regex)
      const isNumberedListItem = /^\d+\.\s+/.test(lineWithBreaks);

      // Check if line starts with >> for right alignment
      const isRightAligned = lineWithBreaks.startsWith(">>");

      // If we were building a list and this line is not a list item, output the list
      if (currentListItems.length > 0 && !isNumberedListItem) {
        addNumberedList();
      }

      // Empty lines (from double newlines) should be rendered as a real gap
      if (isDoubleNewline || lineWithBreaks.trim() === "") {
        // If we have a list in progress, add it before the gap
        if (currentListItems.length > 0) {
          addNumberedList();
        }
        content.push({ text: "", margin: [0, 5, 0, 5] });
      }
      // Handle all other line types
      else if (lineWithBreaks.startsWith("# ")) {
        // Add any pending list before headings
        if (currentListItems.length > 0) {
          addNumberedList();
        }
        content.push({
          text: lineWithBreaks.substring(2),
          style: "title",
          fontFeatures: ["kern"],
        });
      } else if (lineWithBreaks.startsWith("## ")) {
        // Add any pending list before headings
        if (currentListItems.length > 0) {
          addNumberedList();
        }
        content.push({
          text: lineWithBreaks.substring(3),
          style: "sectionTitle",
          fontFeatures: ["kern"],
        });
      } else if (lineWithBreaks.startsWith("### ")) {
        // Add any pending list before headings
        if (currentListItems.length > 0) {
          addNumberedList();
        }
        content.push({
          text: lineWithBreaks.substring(4),
          style: "subSectionTitle",
          fontFeatures: ["kern"],
        });
      }
      // Handle horizontal rule (---, *** or ___)
      else if (/^[-*_]{3,}$/.test(lineWithBreaks.trim())) {
        addNumberedList();
        content.push({
          canvas: [
            {
              type: "line",
              x1: 0,
              y1: 0,
              x2: 515, // Width of the line - slightly less than page width
              y2: 0,
              lineWidth: 1,
              lineColor: "#aaaaaa",
            },
          ],
          margin: [0, 10, 0, 10], // Add some margin for spacing
        });
      }
      // Handle numbered bullet points (e.g., "1. ", "7.⁠ ⁠")
      else if (isNumberedListItem) {
        // Simplified regex to avoid special characters
        // Extract the number and the text
        const parts = lineWithBreaks.split(/^(\d+\.\s+)/);
        const textPart = parts[2] || lineWithBreaks;
        const number = parseInt(parts[1]) || 1;
        const listText = textPart;

        // Process formatted text in numbered list items
        const formattedText = processFormattedText(listText);

        // If this is a new list or a non-consecutive item, start a new list
        if (currentListStartNumber === null) {
          currentListStartNumber = number;
        }

        // Add the item to our current list (with proper spacing)
        currentListItems.push({
          text: formattedText,
          style: "listItem",
          margin: [0, 0, 0, 0], // Minimal margins between list items
          fontFeatures: ["kern"],
        });

        // Check if this is the last line or if the next line is not a list item
        // If so, we need to output the current list
        if (index === lines.length - 1) {
          addNumberedList();
        }
      }
      // Handle bullet points and lists with various formats
      else if (
        lineWithBreaks.startsWith("- ") ||
        lineWithBreaks.startsWith("* ") ||
        lineWithBreaks.startsWith("• ")
      ) {
        // Add any pending numbered list before bullet list
        if (currentListItems.length > 0) {
          addNumberedList();
        }

        // Extract the text after the bullet
        let listText = lineWithBreaks.replace(/^[-*•]\s*/, "");

        // Process formatted text in bullet list items
        const formattedText = processFormattedText(listText);

        content.push({
          ul: [
            {
              text: formattedText,
              style: "listItem",
              fontFeatures: ["kern"],
            },
          ],
          alignment: "justify", // Add justify alignment to bullet lists
        });
      }
      // Handle right-aligned text (starting with >>)
      else if (isRightAligned) {
        // Add any pending list before right-aligned text
        if (currentListItems.length > 0) {
          addNumberedList();
        }

        // Remove the >> marker and process the text
        const rightAlignedText = lineWithBreaks.substring(2);
        const textRuns = processFormattedText(rightAlignedText);

        content.push({
          text: textRuns,
          alignment: "right",
          style: "paragraph",
          margin: [0, 1, 0, 0],
          fontFeatures: ["kern"],
        });
      }
      // Handle regular paragraphs
      else {
        // Add any pending list before paragraphs
        if (currentListItems.length > 0) {
          addNumberedList();
        }

        // Handle label lines (e.g., "_Dosya No_:")
        const isLabelLine = /^_[^_]+_:/.test(lineWithBreaks);

        // Create style name based on whether this has tab indent
        const getStyleName = (baseName) => {
          return hasTabIndent ? `${baseName}Indent` : baseName;
        };

        if (isLabelLine) {
          const labelMatch = lineWithBreaks.match(/^_([^_]+)_:(.*)/);
          if (labelMatch) {
            const labelText = labelMatch[1];
            const valueText = labelMatch[2];

            const textContent = [
              { text: labelText + ": ", style: getStyleName("labelMedium") },
              { text: valueText, style: getStyleName("paragraph") },
            ];

            content.push({
              text: textContent,
              alignment: "justify", // Add justify alignment to label text content
              margin: [0, 1, 0, 0],
              fontFeatures: ["kern"],
            });
          } else {
            const textRuns = processFormattedText(lineWithBreaks);
            content.push({
              text: textRuns,
              style: getStyleName("paragraph"),
              alignment: "justify", // Add justify alignment
              margin: [0, 1, 0, 0],
              fontFeatures: ["kern"],
            });
          }
        } else {
          const textRuns = processFormattedText(lineWithBreaks);
          content.push({
            text: textRuns,
            style: getStyleName("paragraph"),
            alignment: "justify", // Add justify alignment
            margin: [0, 1, 0, 0],
            fontFeatures: ["kern"],
          });
        }
      }
    });

    // Make sure we add any remaining list items
    if (currentListItems.length > 0) {
      addNumberedList();
    }
    const fixQuotes = (s) =>
      s
        .replace(/["\u201C\u201D\u201E]/g, "$&\u2006\u2006") // tırnak + 2×thin space
        .replace(/(_|\*)\s+\*/g, "$1\u2006\u2006*"); // italic sonrası bold için extra space (tek * ile)

    const deepFix = (node) => {
      if (typeof node === "string") return fixQuotes(node);
      if (Array.isArray(node)) return node.map(deepFix);
      if (node && typeof node === "object") {
        const obj = {};
        for (const k in node) obj[k] = deepFix(node[k]);
        return obj;
      }
      return node;
    };

    // Header: image for dogandemir client, text for others (e.g. hergulkir)
    const clientId = getClientId();
    const headerImageBase64 =
      clientId === "dogandemir" ? await imageToBase64(headerImage) : null;

    // Don't add header image to content since we have it in page header
    // This prevents duplicate images on the first page

    // Generate filename
    let filename = "tahkim-davasi";
    if (documentData && selectedTemplateId) {
      // Format: {arbitrationNumber} {applicantName} {selectedTemplateId} CD
      const arbitrationNumber = documentData.arbitrationNumber || "";
      const applicantName = documentData.applicantName || "";

      // Use dictionary value for templateId if available, otherwise use templateId
      const templateDisplayName =
        (dictionary && dictionary[selectedTemplateId]) || selectedTemplateId;

      // Clean the values to make them safe for filenames
      const cleanArbitrationNumber = arbitrationNumber
        .replace(/[<>:"/\\|?*]/g, "")
        .trim();
      const cleanApplicantName = applicantName
        .replace(/[<>:"/\\|?*]/g, "")
        .trim();
      const cleanTemplateDisplayName = templateDisplayName
        .replace(/[<>:"/\\|?*]/g, "")
        .trim();

      if (
        cleanArbitrationNumber ||
        cleanApplicantName ||
        cleanTemplateDisplayName
      ) {
        const parts = [
          cleanArbitrationNumber,
          cleanApplicantName,
          cleanTemplateDisplayName,
          "CD",
        ].filter((part) => part);
        filename = parts.join(" ");
      }
    }
    filename += ".pdf";

    // PDF döküman tanımı
    const docDefinition = {
      content: deepFix(content),
      styles: styles,
      defaultStyle: {
        fontSize: 11,
        font: "TimesNewRoman", // Test with local Times New Roman fonts
        alignment: "justify", // Add default justify alignment for all text
        lineHeight: 1.2, // Better line spacing
      },
      pageSize: "A4",
      pageMargins: [40, 90, 40, clientId === "dogandemir" ? 60 : 85], // hergulkir: more bottom margin for 4-line footer
      // Add metadata including requestId
      info: {
        title: filename.replace(".pdf", ""),
        subject: `RequestId: ${requestId || "unknown"}`,
        author: "Arbitration System",
        creator: "pdfmake",
        requestId: requestId || "unknown",
      },
      // Add header to all pages: image for dogandemir, text for others
      header: headerImageBase64
        ? {
            image: headerImageBase64,
            width: 128,
            height: 42,
            alignment: "center",
            margin: [0, 10, 0, 40],
          }
        : {
            text: [
              { text: "HERGÜL&KIR\n", bold: true },
              { text: "Hukuk ve Danışmanlık", bold: false },
            ],
            alignment: "center",
            margin: [0, 10, 0, 40],
          },
      // Add footer to all pages: dogandemir vs hergulkir (and others)
      footer:
        clientId === "dogandemir"
          ? {
              columns: [
                {
                  text: [
                    {
                      text: "Kaptanpaşa Mah. Halit Ziya Türkkan Sk. No:19 Famas Plaza C blok Kat 9 Şişli/İSTANBUL\n",
                      fontSize: 9,
                      bold: true,
                      alignment: "center",
                    },
                    {
                      text: "tahkim@dogandemir.av.tr      +90 212 416 28 03",
                      fontSize: 9,
                      bold: true,
                      alignment: "center",
                    },
                  ],
                  alignment: "center",
                  margin: [0, 20, 0, 5],
                },
              ],
            }
          : {
              columns: [
                {
                  text: [
                    {
                      text: "Adres: Kuştepe Mah. leylak-1 Sk. No:3 K:1 D:7 Murat İş Merkezi Şişli/İSTANBUL\n",
                      fontSize: 9,
                      bold: false,
                      alignment: "center",
                    },
                    {
                      text: "www.hergulkirhukukvedanismanlik.com\n",
                      fontSize: 9,
                      bold: false,
                      alignment: "center",
                    },
                    {
                      text: "M. 0554 279 37 41\n",
                      fontSize: 9,
                      bold: false,
                      alignment: "center",
                    },
                    {
                      text: "M. 0536 694 21 15",
                      fontSize: 9,
                      bold: false,
                      alignment: "center",
                    },
                  ],
                  alignment: "center",
                  margin: [0, 20, 0, 5],
                },
              ],
            },
      // Font rendering options
      compress: false, // Disable compression for better font rendering
    };

    // Collect additional PDF URLs (additional_document items with pdf extension)
    const additionalPdfUrls = (documentData?.additional_document || [])
      .filter((doc) => doc && doc.extension === "pdf" && doc.url)
      .map((doc) => doc.url);

    // PDF'i oluştur ve indir
    try {
      if (additionalPdfUrls.length > 0) {
        const mainBuffer = await getPdfBuffer(docDefinition);
        await mergeAndDownloadPdfs(mainBuffer, additionalPdfUrls, filename);
      } else {
        pdfMake.createPdf(docDefinition).download(filename);
      }
      console.log(
        "PDF successfully created with font:",
        docDefinition.defaultStyle.font
      );
    } catch (fontError) {
      console.error("Font error, trying fallback:", fontError);
      docDefinition.defaultStyle.font = "Roboto";
      if (additionalPdfUrls.length > 0) {
        const mainBuffer = await getPdfBuffer(docDefinition);
        await mergeAndDownloadPdfs(mainBuffer, additionalPdfUrls, filename);
      } else {
        pdfMake.createPdf(docDefinition).download(filename);
      }
      console.log("PDF created with fallback font: Roboto");
    }

    // Send download log
    await sendDownloadLog(
      userEmail,
      selectedTemplateId,
      "pdf",
      filename,
      requestId
    );

    // Save document data to Firestore before download
    try {
      await upsertRequestDocument(
        requestId,
        documentData,
        documentOrder,
        selectedTemplateId,
        userEmail
      );

      console.log("Document data saved to Firestore successfully");
    } catch (saveError) {
      console.error("Error saving document data to Firestore:", saveError);
      // Continue with download even if save fails
    }

    return { success: true };
  } catch (error) {
    console.error("PDF oluşturma hatası:", error);
    alert("PDF oluşturulamadı. Lütfen tekrar deneyin: " + error.message);
    return { success: false, error: error.message };
  } finally {
    // Reset button state
    const downloadBtn = document.getElementById("download-summary-btn");
    if (downloadBtn) {
      downloadBtn.textContent = "PDF İndir";
      downloadBtn.disabled = false;
    }
  }
};
